import { FC, useContext, useEffect, useMemo, useState } from "react";
import style from "./SpacePage.module.scss";
import Typhography from "../../shared/Typhography/Typhography";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  FlagBannerIcon,
  MagnifyingGlass,
  MagnifyingGlassIcon,
  StarIcon,
} from "@phosphor-icons/react";
import { useEndpoints } from "../../hooks/useEndpoints";
import { useToastAlert } from "../../hooks/useToastAlert/useToastAlert";
import { Menu, Portal } from "@chakra-ui/react";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import { GenderIcon, TypeSpaceIcon } from "../../shared/Icons/Icons";
import { ISpace } from "../../models/Space";
import { SlidersHorizontalIcon } from "@phosphor-icons/react/dist/ssr";
import EmptyContainer from "../../shared/EmptyContainer/EmptyContainer";
import AddSpaceModal from "../../components/TeamsAndSpaces/AddSpaceModal";
import {
  AddTeamModal,
  IAddTeamModalForm,
} from "../../components/SpacePage/AddTeamModal/AddTeamModal";
import { useDebounce } from "../../hooks/useDebounce";
import { ITeam } from "../../models/Team";
import { isAxiosError } from "axios";
import CustomButton from "../../shared/CustomButton/CustomButton";
import TeamCard from "../../components/SpacePage/TeamCard/TeamCard";
import CustomTextInput from "../../shared/CustomInput/CustomTextInput";
import { useDirectorId } from "../../hooks/directorId";
import { getLabelFromOptions } from "../../lib/DurationPicker/utils";
import { GENDER_TEAM_OPTIONS, TYPE_TEAM_OPTIONS } from "../../constants/config";
import { ShowByRole } from "../../shared/ShowByRole";
import { ConfirmModalContext } from "../../providers/confirmModalProvider";
import toast from "react-hot-toast";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";
export const SpacePage: FC = () => {
  const { setState } = useContext(ConfirmModalContext);

  const navigate = useNavigate();
  const { navigate: customNavigate } = useCustomNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { deleteSpace, deleteTeam } = useEndpoints();
  const [isLoadingUpdateTeam, setIsLoadingUpdateTeam] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { getSpace, patchImageTeam, patchTeam } = useEndpoints();
  const { id } = useParams();
  const directorId = useDirectorId();
  const toastAlert = useToastAlert();
  const [space, setSpace] =
    useState<ISpace<ITeam & { users: [{ image: string | null }] }>>();

  const { postTeam } = useEndpoints();
  const [isOpen, setIsOpen] = useState<
    (ITeam & { users: [{ image: string | null }] }) | true
  >();
  const { callbackDebounce } = useDebounce();
  const loadSpace = () =>
    callbackDebounce(
      async () => {
        setIsLoading(true);

        try {
          const { data } = await getSpace(id!, { populateTeams: true });
          setSpace(
            data as unknown as ISpace<
              ITeam & { users: [{ image: string | null }] }
            >
          );
        } catch (error) {
          console.error(error);
          toastAlert({
            type: "error",
            message: "Error",
          });
        } finally {
          setIsLoading(false);
        }
      },
      "load-space",
      60
    );
  const onClose = () => {
    setIsOpen(undefined);
  };

  const goBack = () => {
    navigate("/dashboard/spaces");
  };

  useEffect(() => {
    loadSpace();
  }, []);

  const filteredTeams = useMemo(() => {
    if (!space?.teams || !searchValue) return space?.teams;

    return space.teams.filter((team) =>
      team.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [space?.teams, searchValue]);

  const preferredTeams = useMemo(() => filteredTeams?.filter(t => t.isPreferred) ?? [], [filteredTeams]);
  const otherTeams = useMemo(() => filteredTeams?.filter(t => !t.isPreferred) ?? [], [filteredTeams]);

  const onDeleteSpace = async () => {
    const id = toast.loading(
      `Rimozione spazio ${space?.name || ""} in corso...`,
      { duration: 20000 }
    );
    try {
      await deleteSpace(space!._id);
      toast.success(`${space?.name} rimosso`, {
        id,
      });
      goBack();
    } catch (error) {
      toast.error(`Errore imprevisto`, {
        id,
      });
    }
  };

  const handleDeleteTeam = () => {
    if (!isOpen || typeof isOpen === 'boolean') return;
    const teamName = isOpen.name;
    const teamId = isOpen._id;
    // Chiudi prima AddTeamModal per evitare conflitti tra due Chakra modal aperte insieme
    onClose();
    setState({
      title: 'Elimina team',
      description: `Sei sicuro di voler eliminare il team "${teamName}"? Il team verrà rimosso e gli eventi futuri non ancora compilati verranno aggiornati.`,
      onConfirm: async () => {
        const toastId = toast.loading(`Rimozione team ${teamName} in corso...`, { duration: 20000 });
        try {
          await deleteTeam(teamId);
          toast.success(`${teamName} eliminato`, { id: toastId });
          loadSpace();
        } catch {
          toast.error("Errore durante l'eliminazione del team", { id: toastId });
        }
      },
    });
  };

  const saveTeam = async (
    originalForm: IAddTeamModalForm,
    originalImage?: string,
    logoImage?: { file: File; url: string } | string
  ) => {
    setIsLoadingUpdateTeam(true);
    const { athletes, coaches, ...body } = originalForm;
    const users = [...athletes, ...coaches].map(({ _id }) => _id);
    const form = {
      ...body,
      users,
      spaceId: space?._id,
      name: originalForm.name.trim(),
    };

    let teamId = (isOpen as ITeam)?._id;
    try {
      if (typeof isOpen === "boolean") {
        const { data: createdTeam } = await postTeam({
          ...form,
          directorId: directorId,
        });
        teamId = createdTeam._id;
      } else {
        await patchTeam(isOpen!._id, form);
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (typeof error.response?.data?.error?.[0] === "string") {
          toastAlert({
            type: "error",
            message: error.response?.data?.error?.[0],
          });
        } else if (error.response?.data?.message) {
          toastAlert({
            type: "error",
            message: error.response?.data.message,
          });
        }
      }
      console.error(error);
      setIsLoadingUpdateTeam(false);
      return;
    }

    try {
      if (originalImage === logoImage) {
      } else if (logoImage && typeof logoImage !== "string") {
        const formData = new FormData();
        formData.append("image", logoImage.file);
        await patchImageTeam(teamId, formData);
      } else if (!logoImage && originalImage) {
        await patchImageTeam(teamId, {});
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoadingUpdateTeam(false);
    onClose();
    loadSpace();
  };

  const onChangeStartTeam = (id: string, isPreferred: boolean) => {
    setSpace((prev) => {
      if (!prev) return prev;
      const copyPrev: any = structuredClone(prev!);
      copyPrev.teams = copyPrev.teams.map((team: ITeam) =>
        team._id === id ? { ...team, isPreferred } : team
      );
      return copyPrev;
    });
  };

  if (isLoading) {
    return (
      <div
        style={{ height: "100vh" }}
        className="d-flex flex-1 justify-content-center align-items-center"
      >
        <LeopartLoader />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Typhography>Error</Typhography>
      </div>
    );
  }
  return (
    <>
      <div className={style.container}>
        {/* Back */}
        <div className={style.backLink} onClick={goBack}>
          <ArrowLeftIcon className={style.arrowLeft} />
          <Typhography size="bold-12">Tutti gli spazi</Typhography>
        </div>

        {/* Header */}
        <div className={style.header}>
          <div className={style.headerLeft}>
            <div className={style.headerIconWrapper}>
              <TypeSpaceIcon type={space.type} size={22} />
            </div>
            <div>
              <Typhography type="h1" size="bold-24">{space.name}</Typhography>
              <Typhography size="regular-14" className={style.subtitle}>
                {getLabelFromOptions(TYPE_TEAM_OPTIONS, space.type)}
              </Typhography>
            </div>
            <div className={style.genderChip}>
              <GenderIcon type={space.gender} />
              <Typhography size="regular-12">
                {getLabelFromOptions(GENDER_TEAM_OPTIONS, space.gender)}
              </Typhography>
            </div>
          </div>
          <div className={style.headerRight}>
            {space.teams.length > 0 && (
              <div className={style.countBadge}>
                <Typhography size="bold-12">{space.teams.length}</Typhography>
              </div>
            )}
            <ShowByRole roles={["isAdmin", "isDirector"]}>
              <Menu.Root>
                <Menu.Trigger asChild>
                  <div className="d-flex align-items-center cursor-pointer gap-1">
                    <SlidersHorizontalIcon className={style.sliderHorizontal} />
                    <Typhography>Impostazioni</Typhography>
                  </div>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content className="ps-2 pe-2">
                      <Menu.Item
                        value="delete"
                        className="cursor-pointer"
                        onClick={() => {
                          setState({
                            title: "Attenzione",
                            description:
                              "L'operazione è permanente: verranno rimossi anche i team dello spazio e i loro eventi futuri non compilati.",
                            onConfirm: onDeleteSpace,
                          });
                        }}
                      >
                        Rimuovi Spazio
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </ShowByRole>
          </div>
        </div>

        {/* Toolbar */}
        {space.teams.length > 0 && (
          <div className={style.toolbar}>
            <CustomTextInput
              value={searchValue}
              onChange={({ target: { value } }) => setSearchValue(value)}
              placeholder="Cerca team..."
              rightAddon={<MagnifyingGlassIcon />}
            />
            <ShowByRole roles={["isAdmin", "isDirector"]}>
              <CustomButton
                color="secondary"
                icon="plus"
                iconPosition="left"
                onClick={() => setIsOpen(true)}
              >
                Aggiungi Team
              </CustomButton>
            </ShowByRole>
          </div>
        )}

        {/* Content */}
        {space.teams.length ? (
          <>
            {preferredTeams.length > 0 && (
              <div className={style.preferredSection}>
                <div className={style.sectionHeader}>
                  <StarIcon weight="fill" className={style.sectionStar} />
                  <Typhography size="bold-14">In evidenza</Typhography>
                </div>
                <div className={style.grid}>
                  {preferredTeams.map((team) => (
                    <div
                      key={team._id}
                      className="cursor-pointer"
                      onClick={() => customNavigate("DIRECTOR_TEAM_ID", { spaceId: id!, id: team._id })}
                    >
                      <TeamCard
                        onclickEditButton={(e: any) => { e.stopPropagation(); setIsOpen(team); }}
                        onChangeStart={onChangeStartTeam}
                        patchTeam={patchTeam}
                        team={team as any}
                        spaceType={space.type}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {otherTeams.length > 0 && (
              <div className={style.otherSection}>
                {preferredTeams.length > 0 && (
                  <div className={style.sectionHeader}>
                    <Typhography size="bold-14">Tutte le squadre</Typhography>
                  </div>
                )}
                <div className={style.grid}>
                  {otherTeams.map((team) => (
                    <div
                      key={team._id}
                      className="cursor-pointer"
                      onClick={() => customNavigate("DIRECTOR_TEAM_ID", { spaceId: id!, id: team._id })}
                    >
                      <TeamCard
                        onclickEditButton={(e: any) => { e.stopPropagation(); setIsOpen(team); }}
                        onChangeStart={onChangeStartTeam}
                        patchTeam={patchTeam}
                        team={team as any}
                        spaceType={space.type}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyContainer
            buttonText="Aggiungi team"
            Icon={FlagBannerIcon}
            title="Nessun team presente"
            description="Aggiungine uno per cominciare"
            onClick={() => setIsOpen(true)}
          />
        )}
      </div>
      <AddTeamModal
        currentSpace={space}
        onClose={onClose}
        isLoading={isLoadingUpdateTeam}
        onConfirm={saveTeam}
        onDelete={handleDeleteTeam}
        isOpen={isOpen}
      />
    </>
  );
};

export default SpacePage;
